import { msg } from "@lingui/core/macro";
import { type Component, type JSX, Show } from "solid-js";
import { CloudOff, Lock, SearchX } from "@fleet/icons";
import { useI18n } from "@/providers/i18n-provider";
import { TryAnotherAccount } from "@/features/auth/try-another-account";
import { EmptyState } from "./empty-state";
import { Button } from "./button";

type NotFoundVariant = "not-found" | "no-access" | "offline";

interface NotFoundStateProps {
  variant?: NotFoundVariant;
  /** Entity label (e.g. "list", "task") to customize the copy. */
  entityLabel?: string;
  /** Custom title and description (override the default copy). */
  title?: string;
  description?: string;
  /** Primary action (e.g. go back to home / to the parent list). */
  primaryAction?: { label: string; onClick: () => void };
  /** Optional secondary action (e.g. retry). */
  secondaryAction?: { label: string; onClick: () => void };
  /** Offers switching accounts if the active one doesn't have access. */
  offerAccountSwitch?: boolean;
  class?: string;
  "data-testid"?: string;
}

const ICONS: Record<NotFoundVariant, Component<{ class?: string }>> = {
  "not-found": SearchX,
  "no-access": Lock,
  offline: CloudOff,
};

/**
 * "Not found / no access / offline" state for detail routes whose entity
 * could not be resolved (see `createEntityResolution`). Reuses the premium
 * `EmptyState` (brand-aware illustration, animations behind the
 * reduced-motion guard) and offers default localized copy + clear actions,
 * instead of leaving the screen loading indefinitely.
 */
export const NotFoundState: Component<NotFoundStateProps> = (props) => {
  const { t } = useI18n();
  const variant = (): NotFoundVariant => props.variant ?? "not-found";
  const offerSwitch = (): boolean =>
    props.offerAccountSwitch ?? (variant() === "not-found" || variant() === "no-access");

  const defaultTitle = (): string => {
    switch (variant()) {
      case "no-access": {
        return t(msg`You don't have access`);
      }
      case "offline": {
        return t(msg`Can't load this yet`);
      }
      default: {
        return t(msg`Not found`);
      }
    }
  };

  const defaultDescription = (): string => {
    switch (variant()) {
      case "no-access": {
        return t(msg`This item doesn't exist or you don't have permission to view it.`);
      }
      case "offline": {
        return t(
          msg`You're offline and this hasn't synced to this device yet. Reconnect and try again.`,
        );
      }
      default: {
        return t(msg`This item may have been deleted, or the link is no longer valid.`);
      }
    }
  };

  const action = (): JSX.Element => (
    <div class="flex flex-col items-center gap-3">
      <div class="flex flex-col items-center gap-2 sm:flex-row">
        <Show when={props.primaryAction}>
          {(primary) => (
            <Button
              onClick={() => {
                primary().onClick();
              }}
            >
              {primary().label}
            </Button>
          )}
        </Show>
        <Show when={props.secondaryAction}>
          {(secondary) => (
            <Button
              variant="outline"
              onClick={() => {
                secondary().onClick();
              }}
            >
              {secondary().label}
            </Button>
          )}
        </Show>
      </div>
      <Show when={offerSwitch()}>
        <TryAnotherAccount />
      </Show>
    </div>
  );

  // The CTA the icon points to: the primary action if it exists, otherwise
  // the secondary one. Lets a tap/click on the illustration trigger the same
  // navigation/retry as the button (no dead end).
  const iconCta = (): { label: string; onClick: () => void } | undefined =>
    props.primaryAction ?? props.secondaryAction;

  return (
    <EmptyState
      data-testid={props["data-testid"]}
      icon={ICONS[variant()]}
      title={props.title ?? defaultTitle()}
      description={props.description ?? defaultDescription()}
      action={
        (props.primaryAction ?? props.secondaryAction ?? offerSwitch()) ? action() : undefined
      }
      onIconActivate={iconCta() ? () => iconCta()?.onClick() : undefined}
      iconActionLabel={iconCta()?.label}
      class={props.class}
    />
  );
};
